import type { ComponentProps, ElementType } from 'react';
import { twMerge } from 'tailwind-merge';
import type * as TF from 'type-fest';

/**
 * Type-safe props for the Box component
 */
export type BoxProps<C extends ElementType, Props = TF.EmptyObject> = Props &
  AsProp<C> &
  Omit<ComponentProps<C>, keyof (AsProp<C> & Props)>;

function Box<C extends ElementType = 'div'>(
  { component, children, className, ...props }: BoxProps<C>,
  ref?: ComponentProps<C>['ref'] // Ensures ref type matches the element type
) {
  const Component = component || 'div'; // Default to "div" if no component is provided
  return (
    <Component {...props} ref={ref} className={twMerge('gooey-box', className)}>
      {children}
    </Component>
  );
}

Box.displayName = 'GooeyBox';

export default Box;

/*
|------------------
| Internals
|------------------
*/

/**
 * This type allows specifying a component via the `component` prop.
 */
type AsProp<C extends ElementType> = {
  component?: C;
};
